import { ref } from 'vue'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'

// 문의 카테고리
export const INQUIRY_CATEGORIES = [
  { id: 'service', label: '서비스 이용 문의', icon: '📱' },
  { id: 'point', label: '포인트/생선 문의', icon: '🐟' },
  { id: 'consultation', label: '상담 관련 문의', icon: '💬' },
  { id: 'bug', label: '오류/버그 신고', icon: '🐛' },
  { id: 'suggestion', label: '건의/제안', icon: '💡' },
  { id: 'other', label: '기타 문의', icon: '❓' }
]

export function useInquiry() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 이미지 업로드 (최대 3장)
   */
  const uploadInquiryImages = async (files, inquiryId) => {
    const imageUrls = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const fileName = `${Date.now()}_${i}_${file.name}`
      const imageRef = storageRef(storage, `inquiries/${inquiryId}/${fileName}`)

      await uploadBytes(imageRef, file)
      const url = await getDownloadURL(imageRef)
      imageUrls.push(url)
    }

    return imageUrls
  }

  /**
   * 이미지 삭제
   */
  const deleteInquiryImages = async (imageUrls) => {
    for (const url of imageUrls) {
      try {
        const imageRef = storageRef(storage, url)
        await deleteObject(imageRef)
      } catch (err) {
        console.warn('이미지 삭제 실패:', err)
      }
    }
  }

  /**
   * 모든 문의 조회 (Admin용, 페이지네이션)
   */
  const getAllInquiries = async (limitCount = 20, lastDoc = null, statusFilter = null) => {
    loading.value = true
    error.value = null

    try {
      let q

      if (statusFilter) {
        if (lastDoc) {
          q = query(
            collection(db, 'inquiries'),
            where('status', '==', statusFilter),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(limitCount)
          )
        } else {
          q = query(
            collection(db, 'inquiries'),
            where('status', '==', statusFilter),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          )
        }
      } else {
        if (lastDoc) {
          q = query(
            collection(db, 'inquiries'),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(limitCount)
          )
        } else {
          q = query(
            collection(db, 'inquiries'),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          )
        }
      }

      const snapshot = await getDocs(q)
      const inquiries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return {
        inquiries,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limitCount
      }
    } catch (err) {
      console.error('문의 조회 실패:', err)
      error.value = err.message
      return { inquiries: [], lastDoc: null, hasMore: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * 내 문의 목록 조회 (사용자용)
   */
  const getMyInquiries = async (userId, limitCount = 10, lastDoc = null) => {
    loading.value = true
    error.value = null

    try {
      let q

      if (lastDoc) {
        q = query(
          collection(db, 'inquiries'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(limitCount)
        )
      } else {
        q = query(
          collection(db, 'inquiries'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        )
      }

      const snapshot = await getDocs(q)
      const inquiries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return {
        inquiries,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limitCount
      }
    } catch (err) {
      console.error('내 문의 조회 실패:', err)
      error.value = err.message
      return { inquiries: [], lastDoc: null, hasMore: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * 단일 문의 조회
   */
  const getInquiryById = async (inquiryId) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'inquiries', inquiryId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        }
      }
      return null
    } catch (err) {
      console.error('문의 조회 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * 문의 등록 (사용자)
   */
  const createInquiry = async (inquiryData, imageFiles = []) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      // 먼저 문서 생성 (이미지 없이)
      const docRef = await addDoc(collection(db, 'inquiries'), {
        userId: inquiryData.userId,
        userEmail: inquiryData.userEmail,
        userName: inquiryData.userName || '익명',

        category: inquiryData.category || 'other',
        title: inquiryData.title,
        content: inquiryData.content,

        // 이미지
        images: [],

        // 상태: pending(대기), answered(답변완료)
        status: 'pending',

        // 답변
        answer: '',
        answeredAt: null,
        answeredBy: null,

        createdAt: now,
        updatedAt: now
      })

      // 이미지가 있으면 업로드 후 문서 업데이트
      if (imageFiles.length > 0) {
        const imageUrls = await uploadInquiryImages(imageFiles, docRef.id)
        await updateDoc(docRef, { images: imageUrls })
      }

      return docRef.id
    } catch (err) {
      console.error('문의 등록 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 문의 수정 (사용자 - 답변 전에만 가능)
   */
  const updateInquiry = async (inquiryId, inquiryData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'inquiries', inquiryId)
      await updateDoc(docRef, {
        ...inquiryData,
        updatedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('문의 수정 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 답변 등록 (Admin)
   */
  const answerInquiry = async (inquiryId, answer, adminEmail) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()
      const docRef = doc(db, 'inquiries', inquiryId)

      await updateDoc(docRef, {
        answer,
        status: 'answered',
        answeredAt: now,
        answeredBy: adminEmail,
        updatedAt: now
      })
    } catch (err) {
      console.error('답변 등록 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 문의 삭제 (Admin 또는 사용자 본인)
   */
  const deleteInquiry = async (inquiryId) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'inquiries', inquiryId))
    } catch (err) {
      console.error('문의 삭제 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 대기 중인 문의 수 조회 (Admin 배지용)
   */
  const getPendingCount = async () => {
    try {
      const q = query(
        collection(db, 'inquiries'),
        where('status', '==', 'pending')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.length
    } catch (err) {
      console.error('대기 문의 수 조회 실패:', err)
      return 0
    }
  }

  return {
    loading,
    error,
    uploadInquiryImages,
    deleteInquiryImages,
    getAllInquiries,
    getMyInquiries,
    getInquiryById,
    createInquiry,
    updateInquiry,
    answerInquiry,
    deleteInquiry,
    getPendingCount
  }
}
